import { useCallback } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';

import isEmpty from 'lodash/isEmpty';

import MapPinIcon from '@/components/Icons/MapPinIcon';
import MapPinnedIcon from '@/components/Icons/MapPinnedIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useIsMultipleTeamsEnabled from '@/hooks/useIsMultipleTeamsEnabled';
import useUser from '@/hooks/useUser';
import { WorkspaceSections } from '@/sections/Sidebar/types';
import { objectInsertIf } from '@/utils/collections/insertIf';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { sortPinnedIds } from '../utils';
import { useWorkspaceChainsQuery } from './__generated__/queries.generated';

const POLL_INTERVAL = 15000;

function useChains(poll: boolean = false, teamId?: string) {
  const isMultipleTeamsEnabled = useIsMultipleTeamsEnabled();
  const { data, loading, refetch } = useWorkspaceChainsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
    ...objectInsertIf(teamId, { variables: { teamId } }),
    ...objectInsertIf(isMultipleTeamsEnabled && !teamId, { variables: { all: true } }),
  });

  return { chains: data?.chains || [], loading: isEmpty(data) && loading, refetch };
}

function usePinnedAPI() {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.Chains}-${user.id}`),
  );
  const { chains, loading } = useChains();

  const pinnedChains = chains
    .filter((chain) => pinnedIds.includes(chain.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string, entityName: string) => {
      setPinnedIds((prev) => {
        if (prev.includes(entityId)) {
          return prev.filter((id) => id !== entityId);
        }
        enqueueSnackbar(`Pinned ${entityName} to the sidebar`, { variant: 'success' });
        return [entityId, ...prev];
      });
    },
    [setPinnedIds, enqueueSnackbar],
  );

  const canPinMoreChains = pinnedChains.length < MAX_PINNED_IDS;

  function stopPropagation(ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function getPinActionItem(entityId: string, chainName: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreChains && !isPinned;

    return (
      <Tooltip
        title={
          isPinDisabled
            ? `Limit ${MAX_PINNED_IDS} pins`
            : isPinned
            ? 'Unpin from sidebar'
            : 'Pin to sidebar'
        }
      >
        <Box>
          <IconButton
            onClick={(e) => {
              stopPropagation(e);
              togglePinEntity(entityId, chainName);
            }}
            data-cy="pin-entity-btn"
            disabled={isPinDisabled}
          >
            {isPinned ? <MapPinnedIcon color="inherit" /> : <MapPinIcon color="secondary" />}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return { pinnedIds, togglePinEntity, canPinMoreChains, getPinActionItem, loading, pinnedChains };
}

export { useChains, usePinnedAPI };
